import { IsActiveMatchOptions } from '@angular/router';
import { DateTime, Duration } from 'luxon';
import {
  AddressModel,
  DocumentModel,
  ItinerarySearchResultModel,
  JobDriverModel,
  JobModel,
  JobProgressModel,
  JobSearchResultModel,
  JobTravellerModel,
  LocationModel,
  LocationSearchResultModel,
  OrganisationGroupModel,
  PaymentMethodModel,
  PlanningSolutionChangeSearchResultModel,
  PositionDetailModel,
  ReturnAuthorisationModel,
  ReviewModel,
  ServiceClassModel,
  ServiceLineModel,
  TollSetModel,
  TravellerModel,
  TravellerProfileModel,
  VehicleModel,
  VisitSearchResultModel,
} from './api-model';

export type ActiveVehicleCardSource =
  | { useJobListener: true; positionDetail: PositionDetailModel }
  | { useJobListener: false; job: JobModel };
export interface SearchResult {
  params: any;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  error: any;
  searching: boolean;
}

export interface CustomLocation extends LocationSearchResultModel {
  isFromJobHistory: boolean;
  structuredAddress: AddressModel;
}

export interface ThirdPartyQuoteRequest {
  utm_source: string;
  serviceType: string;
  requestPickup: string;
  pickupLatitude: string;
  pickupLongitude: string;
  dropoffLatitude: string;
  dropoffLongitude: string;
  pickupPlace: string;
  dropoffPlace: string;
}

export interface DriverDocument {
  type: string;
  currentVersion: DocumentModel;
  oldVersions: DocumentModel[];
  versions: DocumentModel[];
  driverId: string;
}

export interface ReturnAuthorisationWithReview {
  returnAuthorisation: ReturnAuthorisationModel;
  review: ReviewModel;
}

export interface DriverPaymentMethodModel extends PaymentMethodModel {
  isDefault: boolean;
  isDisbursementAccountBillingDefault?: boolean;
}

export interface OperatorPaymentMethodModel extends PaymentMethodModel {
  isDefault: boolean;
}

export interface OrganisationGroupWithChildren extends OrganisationGroupModel {
  childOrganisations: Array<any>;
  selected: boolean;
}

import { EntityType } from './enums';

export interface EntityServiceConfiguration {
  entityId: string;
  entityType: EntityType;
  defaultContactNumber: string;
  defaultLocation: any;
  entityGroupId: string;
  entityNetworkId: string;
  entityUserId: string;
  entityOrganisationId: string;
  entityLocation: LocationModel;
  dispatchProviderId: string;
}

export interface Traveller {
  traveller: JobTravellerModel;
  organisationStaffId: string;
  type: TravellerType;
}

export enum TravellerType {
  ORGANISTION_GUEST = 'ORGANISTION_GUEST',
  ORGANISTION_STAFF = 'ORGANISTION_STAFF',
  TRAVELLER = 'TRAVELLER',
  GUEST = 'GUEST',
}

export interface GroupHierarchyItem {
  displayName: string;
  logo: string;
  groupId: string;
  type: string;
  parent: GroupHierarchyItem;
  link: string;
}
export interface TravellerWithSelectedProfile {
  traveller: TravellerModel;
  profile: TravellerProfileModel;
  entityType:
    | 'ORGANISATION_STAFF'
    | 'ORGANISATION_GUEST'
    | 'TRAVELLER'
    | 'GUEST';
}
export interface FuseNavigationItem {
  id?: string;
  title?: string;
  subtitle?: string;
  type: 'aside' | 'basic' | 'collapsable' | 'divider' | 'group' | 'spacer';
  hidden?: (item: FuseNavigationItem) => boolean;
  active?: boolean;
  disabled?: boolean;
  tooltip?: string;
  link?: string;
  externalLink?: boolean;
  target?: '_blank' | '_self' | '_parent' | '_top' | string;
  exactMatch?: boolean;
  groupBackground?: string;
  isActiveMatchOptions?: IsActiveMatchOptions;
  function?: (item: FuseNavigationItem) => void;
  classes?: {
    title?: string;
    subtitle?: string;
    icon?: string;
    wrapper?: string;
    host?: string;
  };
  icon?: string;
  materialIcon?: string;
  badge?: {
    title?: string;
    classes?: string;
    ignoreTranslation?: boolean;
  };
  children?: FuseNavigationItem[];
  meta?: any;
  securityFunctionType?: string;
  securityAction?: string;
  securityFunctions?: string[];
  description?: string;
  fleetOnly?: boolean;
  ignoreTranslation?: boolean;
}

export interface JobStatusUpdateAndProgress {
  latestJobProgress: JobProgressModel;
  latestStatusUpdate: JobSearchResultModel;
  statusColor: { borderColor: string; badgeColor: string };
}

export interface ServiceClassWithLines {
  class: ServiceClassModel;
  lines: ServiceLineModel[];
}

export interface ServiceClassTypeWithLineTypes {
  serviceClass: string;
  serviceLines: string[];
}

export interface CurrencyMaskConfig {
  align: string;
  allowNegative: boolean;
  allowZero: boolean;
  decimal: string;
  precision: number;
  prefix: string;
  suffix: string;
  thousands: string;
  nullable: boolean;
  min?: number;
  max?: number;
  inputMode?: CurrencyMaskInputMode;
  label?: string;
  currencyCode?: string;
}
export enum CurrencyMaskInputMode {
  FINANCIAL,
  NATURAL,
}
export interface ActiveJobDisplay {
  slaColor?: string;
  timeUntilNextSla?: string;
  serviceClassIcon?: string;
  serviceLineIcon?: string;
  startLocality?: string;
  startAddress?: string;
  endLocality?: string;
  endAddress?: string;
  networkService?: string;
  slaStartTime?: string;
  slaStartTimeAMPM?: string;
  slaTextColor?: string;
  slaLevel?: number;
  millisecondsTillSLA3?: number;
  jobId?: string;
  networkConditionIds?: string[];
  travellerFirstName?: string;
  travellerLastName?: string;
  organisationName?: string;
  driverFirstName?: string;
  driverTaxiAuthorityNumber?: string;
  plateNumber?: string;
  medallionNumber?: string;
  eta?: string;
  distance?: string;
  status: string;
  numberOfTravellers?: number;
  expiresIn?: string;
  outerRingStroke?: string;
  startTime?: string;
  delayedBy?: string;
  traveller?: JobTravellerModel;
  driver?: JobDriverModel;
  vehicle?: VehicleModel;
  chatAvailable?: boolean;
  callAvailable?: boolean;
  statusBackground?: string;
  statusColor?: string;
  layoutTemplate?: string;
  statusFlashing?: boolean;
  job?: JobSearchResultModel;
  activeLocationLine1?: string;
  activeLocationLine2?: string;
  startAddressDisplay?: any;
  endAddressDisplay?: any;
  jobStatus?: string;
}

export interface PlanItinerary {
  itinerary: ItinerarySearchResultModel;
  visits: VisitSearchResultModel[];
  jobs: RoutePlanJob[]; // Pre-generated RoutePlanJob objects from service
  color: ColorWithShades;
  totalDeadheadDisplay: string;
  totalDurationDisplay: string;
  totalDistanceDisplay: string;
  latestJobProgress?: JobProgressModel; // Single active progress per itinerary
}

export interface RoutePlanJob {
  type:
    | 'STOP'
    | 'START'
    | 'END'
    | 'DEADHEAD'
    | 'PICKUP_AND_DROPOFF'
    | 'PICKUP'
    | 'DROPOFF';
  startBuffer?: Duration;
  startTime?: DateTime;

  startDwellDuration?: Duration;
  job?: JobSearchResultModel;
  pickupVisit?: VisitSearchResultModel;
  dropOffVisit?: VisitSearchResultModel;
  waypointVisits?: VisitSearchResultModel[];
  itinerary: ItinerarySearchResultModel;
  changes?: PlanningSolutionChangeSearchResultModel[];
  color: ColorWithShades;
  id: string;
  travelDuration?: Duration;
  travelDurationHumanReadable?: string;
  totalTravelDistance?: string;
  endTime?: DateTime;
  endArrivalBuffer?: Duration;
  endDwellDuration?: Duration;
  totalDuration?: Duration;
  totalDurationHumanReadable?: string;
  deadHeadDuration?: Duration;
  deadHeadDurationHumanReadable?: string;
  deadHeadDistance?: string;
  deadHeadDurationToNextVisit?: Duration;
  deadHeadDurationToNextVisitHumanReadable?: string;
  deadHeadDistanceToNextVisit?: string;
  pinned?: boolean;
  timingInfo?: JobTimingInfo; // Timing information in the context of the route plan
  jobProgress?: JobProgressModel; // Real-time progress (only populated for active job)
}

export interface ItineraryTimelinePillDisplay {
  type: 'PICKUP_AND_DROPOFF' | 'PICKUP' | 'DROPOFF';
  startBufferWidth: number;
  startTimePosition: number;
  startTimeDwellWidth: number;
  displayLine1: string;
  displayLine2: string;
  startDeparturePosition: number;
  travelWidth: number;
  totalWidth: number;
  travelTime: string;
  travelDistance: string;
  endTimePosition: number;
  endTimeDwellWidth: number;
  dropoffDepartureGapWidth: number;
  endTimeBufferWidth: number;
  markerWidth: number;
  leftPosition?: number;
  startTime: DateTime;
  endTime: DateTime;
  topPosition: number;
  id: string;
  totalTime: string;
  routePlanJob?: RoutePlanJob;
  deadheadWidth: number;
  deadheadDuration: string;
  deadheadDistance: string;
  jobId?: string; // Reference to parent job ID for drag detection
  // Pre-calculated requested time data for performance
  requestedTimePosition?: number;
  hasRequestedTime?: boolean;
  isRequestedTimeValid?: boolean;
  // FIXED: Add buffer window data for separate visual indicators
  bufferWindowStart?: DateTime;
  bufferWindowEnd?: DateTime;
  bufferStartWidth?: number;
  bufferEndWidth?: number;
}

export interface ItineraryTimeLineItem {
  time?: string;
  type?: string;
  displayLine1: string;
  displayLine2?: string;
  visitId?: string;
  jobId?: string; // Ensured consistent camelCase
  actions?: { label: string; action: string }[];
  menuActions?: string[];
  icon?: string;
  links?: { label: string; link: string; type: string }[];
  visit?: VisitSearchResultModel;
}
export interface ColorWithShades {
  base: string; // Base color in HEX
  shades: string[]; // Array of 5 shades in HEX
}

export interface ColorGeneratorOptions {
  hueRange?: [number, number]; // Range of hues to use (0 to 360)
  saturation?: number; // Saturation level (0 to 1)
  lightness?: number; // Lightness level (0 to 1)
  chromaScaleMode?: 'hcl' | 'lab' | 'lch' | 'hsl' | 'hsv'; // Color space mode
  includeNeutrals?: boolean; // Whether to include neutral colors
  colorBlindFriendly?: boolean; // Whether to prioritize color-blind friendly colors
}

export interface TimingStatus {
  status:
    | 'on-time'
    | 'early'
    | 'late'
    | 'earlier-than-buffer-required'
    | 'earlier-than-buffer-requested';
  differenceMinutes: number;
  displayText: string;
  cssClass: string;
}

export interface VisitTimingInfo {
  // The three key times
  requestedOrRequiredTime?: DateTime; // Customer's desired time
  isRequired: boolean; // True if required vs requested
  expectedTime?: DateTime; // Route planner's calculated time
  actualTime?: DateTime; // What really happened (arrived)

  // Primary variance (only store what's displayed - performance optimization)
  primaryVarianceDuration?: Duration; // The variance being displayed (positive = late, negative = early)
  primaryVarianceType?: 'actual' | 'expected' | 'now' | 'live-eta'; // Which variance is being shown

  // Live driver ETA data (from JobProgressModel, only present if driver active)
  liveEtaLow?: Duration; // Low ETA relative to now
  liveEtaHigh?: Duration; // High ETA relative to now
  liveEtaCalculatedAt?: DateTime; // When ETA was calculated
  activeLocationStatus?: string; // "EN_ROUTE", "ARRIVED", etc from JobProgressModel.activeLocation.status

  // Display helpers (pre-calculated for performance)
  primaryTime?: DateTime;
  primaryTimeLabel?: string; // "Arrived:" or empty
  varianceText?: string; // Single, context-appropriate variance display (pre-formatted)
  varianceCssClass?: string;
  lineColor?: string; // Timeline line color (only populated in visit-timeline component)

  // Flags
  hasLiveEta: boolean;
  isComplete: boolean;
  isCurrentlyLate: boolean; // true if now > requested/required and not complete

  // Legacy fields (deprecated, for backward compatibility during migration)
  /** @deprecated Use requestedOrRequiredTime instead */
  hasRequestedTime?: boolean;
  /** @deprecated Use expectedTime instead */
  hasExpectedTime?: boolean;
  /** @deprecated Use actualTime and isComplete instead */
  hasActualTime?: boolean;
  /** @deprecated Use varianceText and primaryVarianceDuration instead */
  timingStatus?: TimingStatus;
  /** @deprecated Use actualTime instead */
  actualArrival?: DateTime;
  /** @deprecated Use primaryVarianceDuration instead */
  actualVarianceMinutes?: number;
  /** @deprecated Use varianceText instead */
  actualVarianceText?: string;
  /** @deprecated Use varianceCssClass instead */
  actualVarianceCssClass?: string;
  /** @deprecated Use primaryTimeType instead */
  primaryTimeType?: 'actual' | 'expected' | 'requested' | 'required';
}

export interface LiveEtaInfo {
  jobId: string;
  locationId: string; // matches visit.bookingLocationId
  lowEta: Duration;
  highEta: Duration;
  calculatedAt: DateTime;
  activeLocationStatus: string; // "EN_ROUTE", "ARRIVED", etc
  displayEta?: string; // Optional formatted ETA string from JobProgressModel
}

export interface JobOverlapInfo {
  hasIssue: boolean;
  gapMinutes: number; // Negative = overlap, Positive = gap
  deadheadMinutes: number; // Travel time to next/previous job
  bufferMinutes: number; // gap - deadhead
  nextJobId?: string;
  nextJobRef?: string;
  previousJobId?: string; // For timing FROM previous job
  previousJobRef?: string; // For timing FROM previous job
  issueType?: 'overlap' | 'no-buffer' | 'tight-buffer';
  // Pre-calculated display values (no template evaluation needed)
  displayText: string; // "30 min overlap" or "0 min buffer"
  displayIcon: string; // 'error' or 'warning'
  cssClass: string; // 'text-red-700' or 'text-orange-700'
  tooltipText: string; // Full tooltip message
}

export interface JobTimingInfo {
  pickup: VisitTimingInfo;
  dropoff: VisitTimingInfo;
  hasAnyTimingIssues: boolean;

  // Bidirectional timing information
  previousJobTiming?: JobOverlapInfo; // Timing FROM previous job to this one
  nextJobTiming?: JobOverlapInfo; // Timing FROM this job to next one

  // Deprecated: Keep for backwards compatibility (same as nextJobTiming)
  overlapInfo?: JobOverlapInfo;
}

export interface EnrichedVisitSearchResultModel extends VisitSearchResultModel {
  timingInfo?: VisitTimingInfo;
}

export interface EnrichedJobSearchResultModel extends JobSearchResultModel {
  timingInfo?: JobTimingInfo;
}

export interface TimingConfiguration {
  minimumBufferMinutes: number;
  earlyBufferThresholdMinutes: number;
  timingCheckMode: 'required-only' | 'requested-only' | 'both';
  showOverlapInformation: boolean;
  colorCode: 'all' | 'only-issues' | 'none';
} // Color coding: all=green/orange/red, only-issues=orange/red, none=no colors

/** Conflict row in API error `data` for lease-related 409 responses (e.g. vehicle offroad, scheduled transition). */
export interface LeaseConflictData {
  reservationId: string;
  type: string;
  driver: {
    driverId: string;
    firstName: string;
    lastName: string;
    fullName: string;
  };
  lease: {
    leaseId: string;
    reservationStartDate: string;
    reservationEndDate: string;
    status: string;
  };
}

/** Parsed toll set configuration (replaces string `configurationJson` on {@link TollSetModel}). */
export interface TollSetModelWithConfig
  extends Omit<TollSetModel, 'configurationJson'> {
  configurationJson: TollSet;
}

export interface TollSet {
  tollSetId: string;
  countryCode: string;
  regionCode: string;
  cityCode: string;
  status: string;
  effectiveFrom: DateTime;
  effectiveTo: DateTime;
  name: string;
  description: string;
  version: string;
  taxes: TollTax[];
  tollClasses: TollClass[];
  tollRoadGroups: TollRoadGroup[];
  tollRoads: TollRoad[];
  tollSectionGroups: TollSectionGroup[];
  sources?: TollSource[];
}

export interface TollTax {
  name: string;
  taxApplicable: boolean;
  taxInclusive: boolean;
  taxRate: number;
}

export interface TollClass {
  name: string;
  description: string;
}
export interface TollRoadGroup {
  code: string;
  name: string;
  description: string;
  flagfalls?: TollCost[];
  caps?: TollCost[];
  sources?: TollSource[];
}

export interface TollRoad {
  code: string;
  name: string;
  description: string;
  tollRoadGroupCode: string;
  flagfalls?: TollCost[];
  caps?: TollCost[];
  tollJunctions: TollJunction[];
  tollSections: TollSection[];
  sources?: TollSource[];
  polylines?: TollPolyline[];
  gantries?: TollGantry[];
}

export interface TollJunction {
  code: string;
  name: string;
  link: string;
  description?: string;
}

export interface TollSection {
  startCode: string;
  endCode: string;
  name: string;
  description?: string;
  directionOfTravel: string;
  costs: TollCost[];
  polylineCodes: string[];
  gantryCodes: string[];
  tollRoadGroupCode: string;
}

export interface TollSectionGroup {
  code: string;
  name: string;
  description: string;
  caps?: TollCost[];
}

export interface TollSource {
  name: string;
  description: string;
  url: string;
}

export interface TollPolyline {
  code: string;
  polyline: string;
  type: string;
  generateGantry: boolean;
}

export interface TollGantry {
  code: string;
  polygons: string[];
}

export interface TollCost {
  tollClass: string;
  rates: TollRate[];
}

export interface TollRate {
  day: string;
  interval: string;
  amount: number;
}

// ---------------------------------------------------------------------------
// Route planning: job table / pill timeline (client-side view models only)
// ---------------------------------------------------------------------------

/** Narrow display mode union used by `RoutePlanningJobTimeDisplayPipe` and `RoutePlanService`. */
export type TimeDisplayMode =
  | 'expected-with-variance'
  | 'requested-with-variance';

export interface VisitDisplayCircleOverlayData {
  time: string;
  status?: 'early' | 'late' | 'on-time';
  minutesDifference?: number;
  statusText?: string;
}

/** Transformed visit for the itineraries pill timeline (pre-calculated layout). */
export interface VisitDisplay {
  id: string;
  visitId: string;
  visit: VisitSearchResultModel;

  travelFromPreviousWidth: number;
  arrivalBufferWidth: number;
  dwellWidth: number;
  departureBufferWidth: number;

  travelLineClass: string;
  arrivalBufferStyle: string;
  departureBufferStyle: string;
  dwellBackgroundColor: string;
  travelBorderColor: string;

  hasRequestedTime: boolean;
  requestedTimePosition: number;
  requestedTimeClass: string;

  travelDurationText: string;

  showTravelFromPrevious: boolean;
  showArrivalBuffer: boolean;
  showDepartureBuffer: boolean;

  showActualArrivalCircle: boolean;
  showActualDepartureCircle: boolean;
  actualArrivalTimePosition: number;
  actualDepartureTimePosition: number;

  absoluteArrivalTimePosition: number;
  absoluteDepartureTimePosition: number;

  actualArrivalCircleColor: string;
  actualDepartureCircleColor: string;

  arrivalOverlayData: VisitDisplayCircleOverlayData;
  departureOverlayData: VisitDisplayCircleOverlayData;

  showVisitPill: boolean;
  visitPillStartPosition: number;
  visitPillWidth: number;
  visitPillBackgroundColor: string;

  visitPillLeftPosition: number;
  visitContainerLeftPosition: number;
  requestedTimeLineColor: string;

  rawVisit: VisitSearchResultModel;
}

export interface TravelLineSegment {
  id: string;
  fromVisitId: string;
  toVisitId: string;
  startPosition: number;
  endPosition: number;
  width: number;
  color: string;
  duration: string;
}

/** Transformed job row for pill timeline stacking and drag-drop. */
export interface JobDisplay {
  id: string;
  jobId: string;
  routePlanJob: RoutePlanJob;
  leftPosition: number;
  visits: VisitDisplay[];
  totalWidth: number;

  hasJobChannel: boolean;
  channelStartPosition: number;
  channelEndPosition: number;
  channelWidth: number;
  channelHeight: number;

  travelLineSegments: TravelLineSegment[];

  startTime: DateTime;
  endTime: DateTime;
  visitPills: ItineraryTimelinePillDisplay[];

  changeCount: number;
  showBadge: boolean;

  /** Vertical stack lane index from overlap grouping (0-based). */
  stackLane: number;
}

export interface RequestedTimeDisplay {
  id: string;
  jobId: string;
  itineraryId: string;
  requestedTime: DateTime;
  position: number;
  isValid: boolean;
  color: string;
  isSelected: boolean;
  isHovered: boolean;
  visit: VisitSearchResultModel;
}

/** Enriched itinerary row for pill timeline rendering. */
export interface TransformedPlanItinerary {
  planItinerary: PlanItinerary;
  jobs: JobDisplay[];
  requestedTimeDisplays: RequestedTimeDisplay[];
  groupedJobDisplays: JobDisplay[][];
  stackLaneCount: number;
  rowHeightPx: number;

  startTime: DateTime;
  endTime: DateTime;
}
